import {
  IssueType,
  ReportIssueInput,
  useCreateIssueWithMessage,
} from '@gripp/shared-logic';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  ScrollView,
  View,
} from 'react-native';
import { Text, TextInput } from 'react-native-paper';
import { useAutoFocusRef } from '../../../hooks';
import { Spacing } from '../../../styles';
import { Colors } from '../../../themes/colors';
import { Button as CropCircleButton } from '../../crop-circle/Button';
import { themedStyleSheet } from '../../../themes/themedStyleSheet';

export type RequestRoutineModalProps = {
  assetId: string;
  workspaceId: string;
  isWeb?: boolean;
  /** Web only: called when button is pressed. Parent manages overlay state. */
  onOpen?: () => void;
  /** Web only: controls the overlay visibility. Use with onClose. */
  visible?: boolean;
  /** Web only: called when overlay should close. Use with visible. */
  onClose?: () => void;
  /** Disables the request (offline: it is a write). An open modal hides. */
  disabled?: boolean;
};

export const RequestRoutineModal = (props: RequestRoutineModalProps) => {
  const { t } = useTranslation();

  const [routineIdea, setRoutineIdea] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRequestRoutine, setShowRequestRoutine] = useState(false);

  const { mutateAsync } = useCreateIssueWithMessage();

  const elementFocusCallBack = useAutoFocusRef();

  const submit = async () => {
    setIsSubmitting(true);

    const input: ReportIssueInput = {
      assetId: props.assetId,
      severity: 'blue',
      title: 'Routine Request',
      type: IssueType.Feedback,
      message: routineIdea,
      workspaceId: props.workspaceId,
    };

    await mutateAsync(
      { input: input },
      {
        onSettled: async () => {
          handleClose();
          setIsSubmitting(false);
          setRoutineIdea('');
        },
      }
    );
  };

  const handleClose =
    props.visible !== undefined
      ? props.onClose ?? (() => {})
      : props.onClose ?? (() => setShowRequestRoutine(false));

  // Web button-only mode: parent controls overlay visibility
  if (props.isWeb && props.onOpen) {
    return (
      <CropCircleButton
        onPress={props.onOpen}
        variant="rounded"
        style={styles.cropCircleButton}
        state="active"
        disabled={props.disabled}
      >
        {t('routines.requestButton')}
      </CropCircleButton>
    );
  }

  // Web overlay-only mode: rendered at container level so it stays within bounds
  if (props.isWeb && props.visible !== undefined) {
    if (!props.visible) return null;

    return (
      <View style={styles.webContainedOverlay}>
        <View style={styles.webModalCard}>
          <Text style={styles.title}>{t('requestRoutine.header')}</Text>
          <Text style={styles.subtitle}>{t('requestRoutine.instruction')}</Text>
          <TextInput
            style={styles.textInput}
            multiline
            mode="outlined"
            placeholder={t('requestRoutine.placeHolder')}
            outlineStyle={styles.textOutline}
            value={routineIdea}
            onChangeText={setRoutineIdea}
            contentStyle={styles.textInputContent}
            ref={elementFocusCallBack}
          />
          <View style={styles.buttonContainer}>
            <CropCircleButton
              disabled={!routineIdea || isSubmitting}
              variant="rectangular"
              state={routineIdea && !isSubmitting ? 'active' : 'default'}
              onPress={async () => await submit()}
              style={styles.cropCircleButton}
            >
              {t('requestRoutine.apply')}
            </CropCircleButton>
            <CropCircleButton
              variant="rectangular"
              state="default"
              onPress={handleClose}
              style={styles.cropCircleButton}
            >
              {t('topNav.cancel')}
            </CropCircleButton>
          </View>
        </View>
      </View>
    );
  }

  // Mobile (and web fallback): self-contained button + Modal
  return (
    <>
      <CropCircleButton
        onPress={() => setShowRequestRoutine(true)}
        variant="rounded"
        style={styles.cropCircleButton}
        state="active"
        disabled={props.disabled}
      >
        {t('routines.requestButton')}
      </CropCircleButton>
      {/* An open request hides while disabled and comes back with its text
          when the page is writable again. */}
      <Modal
        visible={showRequestRoutine && !props.disabled}
        animationType="fade"
        transparent
        statusBarTranslucent
        onRequestClose={handleClose}
      >
        <View style={styles.mobileOverlay}>
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : undefined}
            keyboardVerticalOffset={40}
            style={styles.mobileCardWrapper}
          >
            <View style={styles.mobileCard}>
              <ScrollView
                style={styles.mobileScroll}
                contentContainerStyle={styles.mobileContentContainer}
                scrollEnabled={true}
                showsVerticalScrollIndicator={true}
                keyboardShouldPersistTaps="handled"
              >
                <Text style={styles.title}>{t('requestRoutine.header')}</Text>
                <Text style={styles.subtitle}>
                  {t('requestRoutine.instruction')}
                </Text>
                <TextInput
                  style={styles.textInput}
                  multiline
                  mode="outlined"
                  placeholder={t('requestRoutine.placeHolder')}
                  outlineStyle={styles.textOutline}
                  value={routineIdea}
                  onChangeText={setRoutineIdea}
                  contentStyle={styles.textInputContent}
                  ref={elementFocusCallBack}
                />
              </ScrollView>
              <View style={styles.mobileFooter}>
                <CropCircleButton
                  disabled={!routineIdea || isSubmitting}
                  variant="rectangular"
                  state={routineIdea && !isSubmitting ? 'active' : 'default'}
                  onPress={async () => await submit()}
                  style={styles.cropCircleButton}
                >
                  {t('requestRoutine.apply')}
                </CropCircleButton>
                <CropCircleButton
                  variant="rectangular"
                  state="default"
                  onPress={handleClose}
                  style={styles.cropCircleButton}
                >
                  {t('topNav.cancel')}
                </CropCircleButton>
              </View>
            </View>
          </KeyboardAvoidingView>
        </View>
      </Modal>
    </>
  );
};

const styles = themedStyleSheet((role) => ({
  textInputContent: {
    fontSize: 16,
    color: role.textStrong,
  },
  textInput: {
    height: 160,
    backgroundColor: role.surface,
  },
  textOutline: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: role.borderControl,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
    color: role.textStrong,
  },
  subtitle: {
    fontSize: 14,
    fontWeight: '400',
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 18,
    color: role.textStrong,
  },
  // Web overlay rendered inside the container (position: absolute relative to container)
  webContainedOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: role.backdrop,
    justifyContent: 'flex-end',
    zIndex: 100,
  },
  webModalCard: {
    width: '95%',
    backgroundColor: role.surface,
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingTop: 24,
    paddingBottom: 24,
    rowGap: 16,
    marginHorizontal: 'auto',
    marginBottom: 16,
  },
  buttonContainer: {
    flexDirection: 'column',
    rowGap: 12,
  },
  mobileOverlay: {
    flex: 1,
    backgroundColor: role.backdrop,
    justifyContent: 'flex-end',
  },
  mobileCardWrapper: {
    width: '100%',
    alignItems: 'center',
  },
  mobileCard: {
    width: '100%',
    backgroundColor: role.surface,
    overflow: 'hidden',
  },
  mobileScroll: {
    maxHeight: 320,
  },
  mobileContentContainer: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingTop: 24,
    paddingBottom: 24,
    backgroundColor: role.surface,
  },
  mobileFooter: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingBottom: 16,
    paddingTop: 8,
    rowGap: 12,
    backgroundColor: role.surface,
  },
  titleIcon: {
    right: 4,
    color: Colors.blue,
  },
  cropCircleButton: {
    height: 58,
    flexGrow: 1,
  },
}));
